import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft, CloudArrowDown, CloudArrowUp, Database, FileZip, ShieldCheck,
  Warning, CheckCircle, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";

export default function DatabaseAdminPage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [audit, setAudit] = useState([]);
  const [confirmFile, setConfirmFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState("replace");
  const fileInputRef = useRef(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backup/preview");
      setPreview(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/backup/audit?limit=15");
      setAudit(data);
    } catch (e) {
      // non-fatal
      console.warn("audit fetch failed:", e.message);
    }
  }, []);

  useEffect(() => { loadPreview(); loadAudit(); }, [loadPreview, loadAudit]);

  const onExport = async () => {
    setExporting(true);
    try {
      const resp = await api.post("/admin/backup/export", null, { responseType: "blob" });
      // pull filename from Content-Disposition
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const fname = m ? m[1] : `flowscribe_backup_${Date.now()}.zip`;
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
      loadAudit();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setExporting(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Only .zip backups are accepted");
      return;
    }
    if (f.size > 200 * 1024 * 1024) {
      toast.error("File exceeds 200 MB limit");
      return;
    }
    setConfirmFile(f);
  };

  const onRestoreConfirm = async () => {
    if (!confirmFile) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", confirmFile);
      fd.append("mode", restoreMode);
      const { data } = await api.post("/admin/backup/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const counts = Object.values(data.restored).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${counts.toLocaleString()} documents (${Object.keys(data.restored).length} collections)`);
      setConfirmFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadPreview();
      await loadAudit();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <Link to="/admin" className="text-xs font-mono text-[--muted] hover:text-[--ink] inline-flex items-center gap-1">
          <ArrowLeft weight="bold" size={12} /> Admin
        </Link>
        <div className="mt-2 overline text-[--muted] inline-flex items-center gap-2">
          <Database weight="bold" size={14} /> DATABASE MANAGEMENT
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">Backup &amp; restore</h1>
        <p className="text-[--muted] mt-3 max-w-2xl text-sm">
          Export the full MongoDB database as a timestamped zip, or restore one. Affects every
          collection except internal system collections. All actions are written to the audit log.
        </p>
      </div>

      {/* Stats card */}
      <section className="hard-border bg-white p-6 mb-10" data-testid="db-stats-card">
        <div className="overline text-[--muted] mb-2">CURRENT DATABASE</div>
        {loading ? (
          <div className="text-sm text-[--muted] font-mono">Loading…</div>
        ) : !preview ? (
          <div className="text-sm text-red-700 font-mono">Could not read database stats.</div>
        ) : (
          <>
            <div className="font-display font-black text-2xl mb-1">{preview.db}</div>
            <div className="text-xs font-mono text-[--muted] mb-4">
              {preview.collections.length} collections · {preview.total_documents.toLocaleString()} documents
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {preview.collections.map((c) => (
                <div key={c.name} className="bg-[--paper] border border-[--line] px-3 py-2" data-testid={`collection-row-${c.name}`}>
                  <div className="font-mono text-xs text-[--muted]">{c.name}</div>
                  <div className="font-display font-black text-xl tabular-nums">{c.count.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <div className="grid md:grid-cols-2 gap-6 mb-10">
        {/* Export */}
        <section className="hard-border bg-white p-6 flex flex-col" data-testid="export-card">
          <div className="flex items-center gap-3 mb-2">
            <CloudArrowDown weight="duotone" size={32} />
            <div>
              <div className="overline text-[--muted]">EXPORT</div>
              <h2 className="font-display font-black text-2xl tracking-tighter">Download backup</h2>
            </div>
          </div>
          <p className="text-xs text-[--muted] mb-5 leading-relaxed">
            Generates <code className="font-mono">flowscribe_backup_YYYYMMDD_HHMMSS.zip</code>
            &nbsp;containing one JSON file per collection plus a manifest. ObjectIds and dates are preserved
            losslessly via extended JSON. Audio files are <strong>not</strong> in this backup.
          </p>
          <button
            onClick={onExport}
            disabled={exporting || loading}
            data-testid="export-btn"
            className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2 mt-auto"
          >
            {exporting ? (
              <>Preparing zip…</>
            ) : (
              <>
                <FileZip weight="bold" size={16} />
                Export &amp; download .zip
              </>
            )}
          </button>
        </section>

        {/* Restore */}
        <section className="hard-border bg-white p-6 flex flex-col" data-testid="restore-card">
          <div className="flex items-center gap-3 mb-2">
            <CloudArrowUp weight="duotone" size={32} />
            <div>
              <div className="overline text-[--muted]">RESTORE</div>
              <h2 className="font-display font-black text-2xl tracking-tighter">Upload backup</h2>
            </div>
          </div>
          <p className="text-xs text-[--muted] mb-5 leading-relaxed">
            Choose a Flowscribe export. Max 200 MB. You'll be asked to confirm whether to
            <strong> replace</strong> existing collections (destructive) or <strong>merge</strong> by document id.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            onChange={onPickFile}
            data-testid="restore-file-input"
            className="block w-full text-xs font-mono file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[--ink] file:text-white file:font-bold file:cursor-pointer hover:file:bg-[--accent] mt-auto"
          />
        </section>
      </div>

      {/* Audit log */}
      <section className="mb-12">
        <div className="overline text-[--muted] mb-3 inline-flex items-center gap-2">
          <ClockCounterClockwise weight="bold" size={14} /> AUDIT LOG
        </div>
        <div className="hard-border bg-white divide-y divide-[--line]">
          {audit.length === 0 ? (
            <div className="p-5 text-xs text-[--muted] font-mono">No actions recorded yet.</div>
          ) : audit.map((row, i) => (
            <div key={i} className="p-4 grid grid-cols-12 gap-3 text-xs items-center" data-testid="audit-row">
              <div className="col-span-12 sm:col-span-3 font-mono text-[--muted]">
                {new Date(row.ts).toLocaleString()}
              </div>
              <div className="col-span-12 sm:col-span-3 font-bold inline-flex items-center gap-2">
                {row.action === "backup_export" && <><CloudArrowDown weight="bold" size={14} /> EXPORT</>}
                {row.action === "backup_restore" && <><CloudArrowUp weight="bold" size={14} /> RESTORE</>}
                {row.action === "backup_restore_failed" && <><Warning weight="bold" size={14} className="text-red-600" /> RESTORE FAILED</>}
                {!["backup_export", "backup_restore", "backup_restore_failed"].includes(row.action) && row.action}
              </div>
              <div className="col-span-12 sm:col-span-3 font-mono text-[--muted] truncate" title={row.admin_email}>{row.admin_email}</div>
              <div className="col-span-12 sm:col-span-3 font-mono text-[--muted] truncate" title={JSON.stringify(row.payload)}>
                {row.payload?.filename || JSON.stringify(row.payload).slice(0, 60)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Confirm modal */}
      {confirmFile && (
        <RestoreConfirmModal
          file={confirmFile}
          mode={restoreMode}
          onModeChange={setRestoreMode}
          onCancel={() => { setConfirmFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
          onConfirm={onRestoreConfirm}
          busy={restoring}
        />
      )}
    </AppShell>
  );
}

function RestoreConfirmModal({ file, mode, onModeChange, onCancel, onConfirm, busy }) {
  const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onCancel}>
      <div
        className="bg-white hard-border max-w-lg w-full p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="restore-confirm-modal"
      >
        <div className="overline text-[--muted] mb-2">CONFIRM RESTORE</div>
        <h2 className="font-display font-black text-2xl tracking-tighter inline-flex items-center gap-2">
          <Warning weight="duotone" size={28} className="text-amber-600" /> Restore database
        </h2>
        <p className="text-xs text-[--muted] mt-2">
          <span className="font-mono">{file.name}</span> · {sizeMB} MB
        </p>

        <div className="mt-5">
          <div className="overline text-[--muted] mb-2">RESTORE MODE</div>
          <div className="grid grid-cols-2 gap-px bg-[--line] border border-[--ink]">
            <label className={`p-4 bg-white cursor-pointer ${mode === "replace" ? "bg-[--accent] text-white" : ""}`} data-testid="mode-replace">
              <input type="radio" name="mode" value="replace" checked={mode === "replace"} onChange={() => onModeChange("replace")} className="sr-only" />
              <div className="font-bold text-sm">Replace</div>
              <div className="text-[11px] font-mono opacity-80 mt-1 leading-snug">Drop every collection then insert from the backup. Destructive but exact.</div>
            </label>
            <label className={`p-4 bg-white cursor-pointer ${mode === "merge" ? "bg-[--accent] text-white" : ""}`} data-testid="mode-merge">
              <input type="radio" name="mode" value="merge" checked={mode === "merge"} onChange={() => onModeChange("merge")} className="sr-only" />
              <div className="font-bold text-sm">Merge</div>
              <div className="text-[11px] font-mono opacity-80 mt-1 leading-snug">Upsert by document id. Keeps records not in the backup. Safer.</div>
            </label>
          </div>
        </div>

        {mode === "replace" && (
          <div className="mt-4 bg-red-50 border border-red-500 p-3 text-xs text-red-800 font-mono leading-relaxed inline-flex items-start gap-2">
            <Warning weight="bold" size={14} className="mt-0.5 shrink-0" />
            <span><strong>This will erase</strong> every collection in the current database before restoring. Make sure you exported a backup first.</span>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onCancel} disabled={busy} data-testid="restore-cancel-btn" className="border border-[--ink] px-4 py-2 text-xs font-bold hover:bg-[--paper] disabled:opacity-50">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="restore-confirm-btn"
            className={`px-4 py-2 text-xs font-bold text-white disabled:opacity-50 inline-flex items-center gap-2 ${mode === "replace" ? "bg-red-600 hover:bg-red-700" : "bg-[--accent] hover:bg-[--accent-hi]"}`}
          >
            {busy ? "Restoring…" : (<><CheckCircle weight="bold" size={14} /> Run restore</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
